import { ThemePreference } from "@kalyzee/kalyzee-ui";
import { LayoutType } from "../interfaces/ui";

export enum StorageCategory {
  TOKEN = 'token',
  SETTINGS = 'settings',
  EXPERT = 'expert',
  MEDIA = "media",
  UI = "ui",
}

export enum TokenType {
  Normal = 'normal',
  Refresh = 'refreshToken',
}

export enum MediaKeys {
  INTPUT_VIDEO_ENABLED = 'intput_video_enabled',
  INTPUT_AUDIO_ENABLED = 'intput_audio_enabled',
  SOURCE_PREFERENCES = 'source_preferences',
}

export enum UiKeys {
  LAYOUT = 'layout',
  THEME = 'theme',
}

export interface StorageKeysMap {
  [StorageCategory.TOKEN]: TokenType,
  [StorageCategory.SETTINGS]: undefined,
  [StorageCategory.EXPERT]: 'home_params',
  [StorageCategory.MEDIA]: MediaKeys,
  [StorageCategory.UI]: UiKeys,
}

const storage = localStorage;

// ---------------- NATIVE ------------------ //

const generateKey = (category : StorageCategory, key?: string) => (
  key ? `${category}_${key}` : category
);

const setItem = <T extends StorageCategory>(
  category : T,
  key: StorageKeysMap[T],
  value: string,
) => storage.setItem(generateKey(category, key), value);

const getItem = <T extends StorageCategory>(
  category : T,
  key?: StorageKeysMap[T],
) => storage.getItem(generateKey(category, key));

// ---------------- TOKEN ------------------ //

export const setToken = (
  token?: string,
  tokenType: TokenType = TokenType.Normal,
) => {
  if (token) setItem(StorageCategory.TOKEN, tokenType, token);
  else storage.removeItem(generateKey(StorageCategory.TOKEN, tokenType));
};

export const getToken = (
  tokenType: TokenType = TokenType.Normal,
) => getItem(StorageCategory.TOKEN, tokenType);

// ---------------- SETTINGS ------------------ //

export const setSettings = (
  settings: string
) => setItem(StorageCategory.SETTINGS, undefined, settings);

export const getSettings = () => getItem(StorageCategory.SETTINGS);

// ---------------- EXPERT ------------------ //

export interface StorageExpertHomeParams {
  micNotSynced?: boolean;
}

export const setExpertHomeParams = (
  params: StorageExpertHomeParams
) => setItem(StorageCategory.EXPERT, "home_params", JSON.stringify(params));

export const getExpertHomeParams = (): StorageExpertHomeParams | undefined => {
  const str = getItem(StorageCategory.EXPERT, "home_params");
  if (!str) return undefined;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return undefined;
};

// ---------------- Media ------------------ //

export interface SourcePreference {
  audioEnabled?: boolean;
  videoEnabled?: boolean;
}

export interface MediaData {
  [MediaKeys.INTPUT_VIDEO_ENABLED]: boolean;
  [MediaKeys.INTPUT_AUDIO_ENABLED]: boolean;
  [MediaKeys.SOURCE_PREFERENCES]: { [sourceName: string]: SourcePreference };
}

export const setMediaParam = <T extends MediaKeys>(
  key: T,
  data: MediaData[T]
) => setItem(StorageCategory.MEDIA, key, JSON.stringify(data));

export const getMediaParam = <T extends MediaKeys>(key: T): MediaData[T] | null => {
  const str = getItem(StorageCategory.MEDIA, key);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return null;
};

// ---------------- UI ------------------ //

export interface UiData {
  [UiKeys.LAYOUT]: LayoutType;
  [UiKeys.THEME]: ThemePreference;
}

export const setUiParam = <T extends UiKeys>(
  key: T,
  data: UiData[T]
) => setItem(StorageCategory.UI, key, JSON.stringify(data));

export const getUiParam = <T extends UiKeys>(key: T): UiData[T] | null => {
  const str = getItem(StorageCategory.UI, key);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return null;
};

// ---------------- Theme (UI shortcuts) ------------------ //

const setTheme = (theme: ThemePreference) => setUiParam(UiKeys.THEME, theme);

const getTheme = (): ThemePreference | null => getUiParam(UiKeys.THEME);

const Storage = {
  // TOKEN
  setToken,
  getToken,
  // SETTINGS
  setSettings,
  getSettings,
  // EXPERT
  setExpertHomeParams,
  getExpertHomeParams,
  // MEDIA
  setMediaParam,
  getMediaParam,
  // UI
  setUiParam,
  getUiParam,
  // THEME
  setTheme,
  getTheme,
};

export default Storage;
